// On-Track Technologies — the time interval every dated chart offers.
//
// Standard 1.1: wherever time is on an axis, the reader picks the grain —
// Days, Weeks, Months, Quarters, Years. This is the one place that vocabulary
// lives, so every chart in the fork buckets and labels a date the same way.
//
//   bucketOf(iso, id)  → the bucket key a date falls in ("2026-09-24",
//                        "2026-W39", "2026-09", "2026-Q3", "2026")
//   labelOf(key, id)   → what to print under the bar
//   bucketsBetween(a, b, id) → every bucket from a to b, so an empty period
//                        still gets its slot and a gap reads as a gap
//   pickFor(spanDays)  → a sensible default grain for a span

export const INTERVALS = Object.freeze([
  { id: 'day', label: 'Days' },
  { id: 'week', label: 'Weeks' },
  { id: 'month', label: 'Months' },
  { id: 'quarter', label: 'Quarters' },
  { id: 'year', label: 'Years' },
]);

// THE GRAINS A GANTT OFFERS, which are not all five.
//
// A bar chart of a measure reads at any grain: a year of revenue in one
// column is still a number. A BAR is different — it is a LENGTH, and the
// reader is asking when the work lands. At quarters a fortnight of work is
// four pixels wide, which is not a plan but a smudge; at years it is not
// there at all. So a timeline offers Days, Weeks and Months and stops.
//
// DAYS IS THE DEFAULT. Weeks was, on the reasoning that a plan is agreed and
// chased in weeks — true of the conversation, and not of the work. What a
// reader opens this chart to see is whether a bar clears a particular DAY,
// and at Days it is also the only grain where the weekend shading means
// anything. The choice sticks per reader the moment they make one, so a
// default is only ever the first answer.
export const GANTT_GRAINS = Object.freeze(['day', 'week', 'month']);
export const GANTT_INTERVALS = Object.freeze(INTERVALS.filter((i) => GANTT_GRAINS.includes(i.id)));
export const DEFAULT_GANTT_GRAIN = 'day';

// A grain a chart may actually draw: what was asked for when it is one of
// the three, otherwise Weeks. A grain arrives from a saved setting or from
// localStorage, either of which can hold something an older build wrote —
// "quarter" is not a crash, it is a fall back to the default.
export const ganttGrain = (asked) => (GANTT_GRAINS.includes(asked) ? asked : DEFAULT_GANTT_GRAIN);

const pad = (n) => String(n).padStart(2, '0');
const parse = (iso) => {
  const s = String(iso || '').slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(Date.UTC(y, m - 1, d));
};
const iso = (dt) => `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;

// ISO week (Monday-based), the way a plan is usually read.
function isoWeek(dt) {
  const t = new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate()));
  const day = (t.getUTCDay() + 6) % 7;          // Mon = 0
  t.setUTCDate(t.getUTCDate() - day + 3);        // the Thursday of this week
  const firstThu = new Date(Date.UTC(t.getUTCFullYear(), 0, 4));
  const fday = (firstThu.getUTCDay() + 6) % 7;
  firstThu.setUTCDate(firstThu.getUTCDate() - fday + 3);
  const week = 1 + Math.round((t - firstThu) / (7 * 86400000));
  return { year: t.getUTCFullYear(), week };
}

export function bucketOf(isoDate, id = 'month') {
  const dt = parse(isoDate);
  if (!dt) return '';
  const y = dt.getUTCFullYear(); const m = dt.getUTCMonth() + 1;
  if (id === 'day') return iso(dt);
  if (id === 'week') { const w = isoWeek(dt); return `${w.year}-W${pad(w.week)}`; }
  if (id === 'quarter') return `${y}-Q${Math.floor((m - 1) / 3) + 1}`;
  if (id === 'year') return `${y}`;
  return `${y}-${pad(m)}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function labelOf(key, id = 'month') {
  if (!key) return '';
  if (id === 'day') { const dt = parse(key); return dt ? `${dt.getUTCDate()} ${MONTHS[dt.getUTCMonth()]}` : key; }
  if (id === 'week') return key.replace('-W', ' wk ');
  if (id === 'quarter') return key.replace('-', ' ');
  if (id === 'year') return key;
  const [y, m] = key.split('-').map(Number);
  return m ? `${MONTHS[m - 1]} ${String(y).slice(2)}` : key;
}

// Every bucket between two dates, inclusive — so a period with nothing in it
// still takes its place on the axis instead of the line jumping over it.
export function bucketsBetween(fromIso, toIso, id = 'month') {
  const a = parse(fromIso); const b = parse(toIso);
  if (!a || !b || a > b) return [];
  const out = []; const seen = new Set();
  const step = id === 'day' ? 1 : id === 'week' ? 7 : 1;
  const cur = new Date(a.getTime());
  let guard = 0;
  while (cur <= b && guard < 4000) {
    const k = bucketOf(iso(cur), id);
    if (k && !seen.has(k)) { seen.add(k); out.push(k); }
    if (id === 'month') cur.setUTCMonth(cur.getUTCMonth() + 1);
    else if (id === 'quarter') cur.setUTCMonth(cur.getUTCMonth() + 3);
    else if (id === 'year') cur.setUTCFullYear(cur.getUTCFullYear() + 1);
    else cur.setUTCDate(cur.getUTCDate() + step);
    guard += 1;
  }
  const last = bucketOf(iso(b), id);
  if (last && !seen.has(last)) out.push(last);
  return out;
}

// A grain that will not produce 400 columns for a five-year span.
export function pickFor(spanDays) {
  if (!Number.isFinite(spanDays)) return 'month';
  if (spanDays <= 31) return 'day';
  if (spanDays <= 120) return 'week';
  if (spanDays <= 900) return 'month';
  if (spanDays <= 2500) return 'quarter';
  return 'year';
}

// N days either side of a date, as an ISO day string. UTC throughout, so a
// timezone never shifts a plan by a day. The timeline's window and the
// bar-drag maths both walk dates with it.
export function addDays(isoDate, n) {
  const dt = parse(isoDate);
  if (!dt) return '';
  return iso(new Date(dt.getTime() + n * 86400000));
}

export function daysBetween(fromIso, toIso) {
  const a = parse(fromIso); const b = parse(toIso);
  if (!a || !b) return null;
  return Math.round((b - a) / 86400000);
}
